// Screen tools — one full-screen and one download path that behave correctly on
// every surface she runs on: phone, tablet, computer, Steam Deck, smart TV and
// game consoles.
//
// Full screen: uses the standard Fullscreen API with WebKit/MS prefixes, falls
// back to iOS video-style presentation notes when the API is absent.
// Download: same file, three routes — File System Access picker where allowed,
// the share sheet on iOS/Android where downloads are blocked, and a plain
// anchor everywhere else (Steam Deck's browser and TV browsers included).
//
// Watermark: Sophia · Skye-Net · local-first.

export type Surface = "phone" | "tablet" | "computer" | "steamdeck" | "tv" | "console";

type FsDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitCurrentFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
};
type FsEl = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitEnterFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

/** Best guess at what the user is holding, from UA + screen + input hints. */
export function detectSurface(): Surface {
  if (typeof window === "undefined") return "computer";
  const ua = navigator.userAgent.toLowerCase();
  const w = window.screen?.width ?? window.innerWidth;
  if (/steamdeck|valve|holo|steam\b/.test(ua)) return "steamdeck";
  if (/playstation|xbox|nintendo|switch/.test(ua)) return "console";
  if (/tizen|web0s|webos|smart-?tv|smarttv|hbbtv|netcast|viera|bravia|aft[bms]|googletv|android tv|crkey/.test(ua)) return "tv";
  if (/ipad|tablet|playbook|silk/.test(ua) || (/android/.test(ua) && !/mobile/.test(ua))) return "tablet";
  if (/iphone|ipod|android.*mobile|windows phone|mobile safari/.test(ua) || w <= 820) return "phone";
  // Steam Deck's desktop browser reports Linux at 1280×800 with touch.
  if (/linux/.test(ua) && (window.screen?.width === 1280 && window.screen?.height === 800)) return "steamdeck";
  if (w >= 1900 && /tv|large/.test(ua)) return "tv";
  return "computer";
}

export function surfaceLabel(s: Surface = detectSurface()): string {
  return s === "steamdeck" ? "Steam Deck"
    : s === "tv" ? "TV"
    : s === "console" ? "console"
    : s === "phone" ? "phone"
    : s === "tablet" ? "tablet"
    : "computer";
}

export function isFullscreen(): boolean {
  if (typeof document === "undefined") return false;
  const d = document as FsDoc;
  return Boolean(
    document.fullscreenElement ?? d.webkitFullscreenElement ?? d.webkitCurrentFullScreenElement ?? d.msFullscreenElement,
  );
}

export function fullscreenSupported(): boolean {
  if (typeof document === "undefined") return false;
  const el = document.documentElement as FsEl;
  return Boolean(el.requestFullscreen ?? el.webkitRequestFullscreen ?? el.msRequestFullscreen ?? el.webkitEnterFullscreen);
}

/** Enter full screen on the given element (defaults to the whole app). */
export async function enterFullscreen(target?: HTMLElement | null): Promise<boolean> {
  if (typeof document === "undefined") return false;
  const el = (target ?? document.documentElement) as FsEl;
  try {
    if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: "hide" });
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) await el.msRequestFullscreen();
    else if (el.webkitEnterFullscreen) await el.webkitEnterFullscreen();
    else return false;
  } catch {
    return false;
  }
  // Landscape lock is a bonus on phones; never fatal when refused.
  try {
    const orient = window.screen?.orientation as (ScreenOrientation & { lock?: (o: string) => Promise<void> }) | undefined;
    if (orient?.lock && detectSurface() === "phone") await orient.lock("landscape").catch(() => undefined);
  } catch { /* refused */ }
  return true;
}

export async function exitFullscreen(): Promise<void> {
  if (typeof document === "undefined") return;
  const d = document as FsDoc;
  try {
    if (document.exitFullscreen) await document.exitFullscreen();
    else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
    else if (d.msExitFullscreen) await d.msExitFullscreen();
  } catch { /* already out */ }
  try { window.screen?.orientation?.unlock?.(); } catch { /* fine */ }
}

export async function toggleFullscreen(target?: HTMLElement | null): Promise<boolean> {
  if (isFullscreen()) { await exitFullscreen(); return false; }
  return enterFullscreen(target);
}

export function subscribeFullscreen(cb: (on: boolean) => void): () => void {
  if (typeof document === "undefined") return () => undefined;
  const fire = () => cb(isFullscreen());
  const events = ["fullscreenchange", "webkitfullscreenchange", "MSFullscreenChange"];
  events.forEach((e) => document.addEventListener(e, fire));
  fire();
  return () => events.forEach((e) => document.removeEventListener(e, fire));
}

// ------------------------------------------------------------------ download

function safeName(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim().slice(0, 120) || "sophia-file";
}

type PickerWindow = Window & {
  showSaveFilePicker?: (opts: { suggestedName?: string; types?: unknown[] }) => Promise<{
    createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }>;
  }>;
};

export interface SaveResult { ok: boolean; how: "picker" | "share" | "anchor" | "none"; note: string }

/** Save a blob to the device, choosing the route that actually works here. */
export async function saveBlob(blob: Blob, filename: string): Promise<SaveResult> {
  const name = safeName(filename);
  const win = window as PickerWindow;

  // 1. Real save dialog — desktop Chrome/Edge, Steam Deck desktop mode.
  if (typeof win.showSaveFilePicker === "function") {
    try {
      const handle = await win.showSaveFilePicker({ suggestedName: name });
      const w = await handle.createWritable();
      await w.write(blob);
      await w.close();
      return { ok: true, how: "picker", note: `Saved ${name}.` };
    } catch (e) {
      // User cancelled — that is not a failure worth falling through for.
      if (e instanceof DOMException && e.name === "AbortError") {
        return { ok: false, how: "picker", note: "Save cancelled." };
      }
    }
  }

  // 2. Share sheet — iOS Safari and most in-app browsers where anchors die.
  try {
    const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
    const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
    if (nav.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: name });
      return { ok: true, how: "share", note: `Shared ${name} — choose “Save to Files”.` };
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      return { ok: false, how: "share", note: "Share cancelled." };
    }
  }

  // 3. Plain anchor — TVs, consoles, Firefox, everything else.
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { ok: true, how: "anchor", note: `Downloading ${name}.` };
  } catch {
    return { ok: false, how: "none", note: "This browser blocked the download." };
  }
}

export function saveText(text: string, filename: string, type = "text/plain;charset=utf-8"): Promise<SaveResult> {
  return saveBlob(new Blob([text], { type }), filename);
}

export function saveJson(data: unknown, filename: string): Promise<SaveResult> {
  return saveText(JSON.stringify(data, null, 2), filename, "application/json");
}

/** Fetch a remote file and hand it to the device saver (used for audio tracks). */
export async function saveRemote(url: string, filename: string): Promise<SaveResult> {
  try {
    const res = await fetch(url);
    if (!res.ok) return { ok: false, how: "none", note: `Source refused (${res.status}).` };
    return saveBlob(await res.blob(), filename);
  } catch {
    // Cross-origin refusals still work as a direct link on most surfaces.
    try {
      window.open(url, "_blank", "noopener");
      return { ok: true, how: "anchor", note: "Opened the file in a new tab to save it." };
    } catch {
      return { ok: false, how: "none", note: "Could not reach that file." };
    }
  }
}
